/**
 * Cache utilities for managing CRUD data refresh
 */

import { getSources } from '../store/sourcesSlice';
import { getSuppliers } from '../store/suppliersSlice';
import { getPlatforms } from '../store/platformsSlice';
import { getSensors } from '../store/sensorsSlice';
// Note: getChannels not imported yet as it doesn't have caching implemented

/**
 * Refresh all CRUD data by forcing fresh API calls
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise} Promise that resolves when all data is refreshed
 */
export const refreshAllCrudData = async (dispatch) => {
  try {
    // Force refresh all CRUD data by passing forceRefresh = true
    const refreshPromises = [
      dispatch(getSources(true)), // forceRefresh = true
      dispatch(getSuppliers(true)),
      dispatch(getPlatforms(true)),
      dispatch(getSensors(true)),
      // TODO: Add remaining slices when they have caching implemented
    ];

    // Wait for all refreshes to complete
    await Promise.all(refreshPromises);

    return { success: true, message: 'All data refreshed successfully' };
  } catch (error) {
    console.error('Error refreshing CRUD data:', error);
    return { success: false, message: 'Failed to refresh some data' };
  }
};

/**
 * Check if user has been active recently
 * @param {number} lastActivityTime - Timestamp of last user activity
 * @param {number} threshold - Threshold in milliseconds (default: 30 seconds)
 * @returns {boolean} True if user has been active recently
 */
export const isUserActive = (lastActivityTime, threshold = 30 * 1000) => {
  return Date.now() - lastActivityTime < threshold;
};

/**
 * Smart refresh that only refreshes if user is active and cache is stale
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} state - Current Redux state
 * @param {number} lastActivityTime - Timestamp of last user activity
 * @returns {Promise} Promise that resolves when refresh is complete (if needed)
 */
export const smartRefreshIfNeeded = async (dispatch, state, lastActivityTime) => {
  // Only refresh if user has been active recently
  if (!isUserActive(lastActivityTime)) {
    return { success: true, message: 'User inactive, skipping refresh' };
  }

  const now = Date.now();
  const needsRefresh = [];

  // Check each slice to see if it needs refresh
  if (state.sources.cached && state.sources.lastFetched &&
      (now - state.sources.lastFetched) >= state.sources.cacheTimeout) {
    needsRefresh.push(() => dispatch(getSources(true)));
  }

  if (state.suppliers.cached && state.suppliers.lastFetched &&
      (now - state.suppliers.lastFetched) >= state.suppliers.cacheTimeout) {
    needsRefresh.push(() => dispatch(getSuppliers(true)));
  }

  if (state.platforms.cached && state.platforms.lastFetched &&
      (now - state.platforms.lastFetched) >= state.platforms.cacheTimeout) {
    needsRefresh.push(() => dispatch(getPlatforms(true)));
  }

  if (state.sensors.cached && state.sensors.lastFetched &&
      (now - state.sensors.lastFetched) >= state.sensors.cacheTimeout) {
    needsRefresh.push(() => dispatch(getSensors(true)));
  }

  // TODO: Add remaining slices when they have caching implemented

  // Execute needed refreshes
  if (needsRefresh.length > 0) {
    try {
      await Promise.all(needsRefresh.map(refresh => refresh()));
      return { success: true, message: `Refreshed ${needsRefresh.length} data sources` };
    } catch (error) {
      console.error('Error in smart refresh:', error);
      return { success: false, message: 'Failed to refresh some data' };
    }
  }

  return { success: true, message: 'No refresh needed' };
};
